import './global.css';

import { AppProps } from 'next/app';
import { NextPage } from 'next';
import Head from 'next/head';
import { createClient, defaultExchanges, Provider as UrqlProvider } from 'urql';
import { ubieAuthStorage } from '~/utils/storage/inquiryUbieAppStorage';
import { operationNameExchange } from '~/utils/operationNameExchange';
import { ErrorBoundaryHandler } from '~/components/errors/error-boundary-handler';
import { PageViewWatcher } from '~/components/features/page-view/PageViewWatcher';
import { NextSeo } from 'next-seo';
import { MedicoUserAccount } from '~/components/pages/medico-user-account/MedicoUserAccount';
import { DeveloperBanner } from '~/components/features/developer-banner/DeveloperBanner';
import { medicoUserAuthStorage } from '~/utils/storage/ubieAppStorage';
import { useIsMedinaviBackendPath } from '~/hooks/useIsMedinaviBackendPath';
import { useRouter } from 'next/router';

const urqlClientForMacken = createClient({
  url: process.env.NEXT_PUBLIC_MACKEN_URL as string,
  fetchOptions: () => {
    const { accessToken } = ubieAuthStorage.get() ?? {};
    if (!accessToken) {
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
  },
  exchanges: [operationNameExchange, ...defaultExchanges],
});

const urqlClientForMedinaviBackend = createClient({
  url: process.env.NEXT_PUBLIC_MEDINAVI_BACKEND_URL as string,
  fetchOptions: () => {
    const auth = medicoUserAuthStorage.get();
    // `ubie-auth` は inquiry 側の ubieAuthStorage と共有しており authSession を持たない形状もありうる
    const accessToken = auth?.authSession?.accessToken;
    if (accessToken == null) return {};
    return {
      headers: {
        'X-Ubie-Authorization': `Bearer ${accessToken}`,
      },
    };
  },
  exchanges: [operationNameExchange, ...defaultExchanges],
});

// macken から medinavi に移植するパスを登録する
const useRequestToMedinaviBackend = () => {
  const router = useRouter();

  return (
    !!router.pathname.match(/\/monshin\/\[keyOrId\]\/signup/) ||
    !!router.pathname.match(/\/medical_institution\/introduction/) ||
    !!router.pathname.match(/\/redirect\/medical-institution-link/)
  );
};

const App: NextPage<AppProps> = ({ Component, pageProps }) => {
  const isMedinaviBackendPath = useIsMedinaviBackendPath();
  const requestToMedinaviBackend = useRequestToMedinaviBackend() || isMedinaviBackendPath;

  return (
    <>
      <Head>
        <meta name="viewport" content="viewport-fit=cover,width=device-width,initial-scale=1.0,minimum-scale=1.0" />
      </Head>
      <NextSeo
        defaultTitle="スマートフォン問診 by ユビー"
        titleTemplate="%s | ユビー"
        noindex
        openGraph={{
          siteName: 'スマートフォン問診 by ユビー',
          type: 'website',
        }}
      />
      <ErrorBoundaryHandler>
        <UrqlProvider value={requestToMedinaviBackend ? urqlClientForMedinaviBackend : urqlClientForMacken}>
          <PageViewWatcher>
            <main>
              {isMedinaviBackendPath && <DeveloperBanner />}
              <MedicoUserAccount>
                <Component {...pageProps} />
              </MedicoUserAccount>
            </main>
          </PageViewWatcher>
        </UrqlProvider>
      </ErrorBoundaryHandler>
    </>
  );
};

export default App;
